import React, { useState } from 'react';
import styles from './SpendingGuessModal.module.css';
import Button from './Button';

interface SpendingGuessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (selectedRange: string) => void;
}

const SpendingGuessModal: React.FC<SpendingGuessModalProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const [selectedRange, setSelectedRange] = useState<string>('');
  const [showComparison, setShowComparison] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const spendingOptions = [
    { id: '1', label: '1. $4,000 or less', value: '4k-or-less', midPoint: 4000 },
    { id: '2', label: '2. $5,000', value: '5k', midPoint: 5000 },
    { id: '3', label: '3. $6,000', value: '6k', midPoint: 6000 },
    { id: '4', label: '4. $7,000+', value: '7k-plus', midPoint: 7000 }
  ];

  // Get monthly income from yearly income quest data
  const getMonthlyIncome = (): number => {
    const yearlyIncomeBudget = localStorage.getItem('yearly_income_budget');
    if (yearlyIncomeBudget) {
      try {
        const budgetData = JSON.parse(yearlyIncomeBudget);
        return budgetData.monthly || 0;
      } catch (error) {
        console.error('Error parsing yearly income budget data:', error);
      }
    }
    return 0;
  };

  // Calculate comparison status
  const getComparisonStatus = (spendingAmount: number, monthlyIncome: number) => {
    if (monthlyIncome === 0) {
      return { color: 'gray', message: 'Complete the "Your Yearly Income" quest first to see this comparison.' };
    }

    if (spendingAmount > monthlyIncome) {
      return {
        color: 'red',
        message: 'Your estimated spending exceeds your monthly income. This could lead to debt accumulation.'
      };
    }

    const percentageDifference = ((monthlyIncome - spendingAmount) / monthlyIncome) * 100;

    if (percentageDifference < 20) {
      return {
        color: 'orange',
        message: 'Your spending is close to your income. Consider building more savings room in your budget.'
      };
    }

    return {
      color: 'green',
      message: 'Great! You have good room in your budget for savings and unexpected expenses.'
    };
  };

  if (!isOpen) {
    return null;
  }

  const handleComplete = () => {
    if (selectedRange) {
      // Get the midpoint value for the selected range
      const selectedOption = spendingOptions.find(option => option.value === selectedRange);
      const spendingAmount = selectedOption?.midPoint || 0;

      // Store the spending amount (midpoint) in localStorage
      localStorage.setItem('monthly_spending_guess', spendingAmount.toString());

      // Show comparison screen first
      setShowComparison(true);
    }
  };

  const handleComparisonComplete = () => {
    setShowComparison(false);
    setShowSuccess(true);
    // Start celebration animation
    setTimeout(() => {
      onComplete(selectedRange);
      setShowSuccess(false);
      setSelectedRange('');
    }, 3000); // Show congratulations for 3 seconds
  };

  const handleClose = () => {
    setSelectedRange('');
    setShowComparison(false);
    setShowSuccess(false);
    onClose();
  };

  if (showSuccess) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.successContainer}>
            <div className={styles.fireworks}>
              <div className={styles.firework}></div>
              <div className={styles.firework}></div>
              <div className={styles.firework}></div>
            </div>
            <h2 className={styles.congratsTitle}>Great Job!</h2>
            <p className={styles.congratsMessage}>
              Building spending awareness is the first step to better financial health!
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (showComparison) {
    const monthlyIncome = getMonthlyIncome();
    const spendingAmount = parseInt(localStorage.getItem('monthly_spending_guess') || '0');
    const comparison = getComparisonStatus(spendingAmount, monthlyIncome);

    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.comparisonContainer}>
            <h2 className={styles.comparisonTitle}>How does your spending compare?</h2>

            <div className={styles.comparisonDetails}>
              <div className={styles.comparisonRow}>
                <span className={styles.comparisonLabel}>Your estimated monthly spending:</span>
                <span className={styles.comparisonValue}>${spendingAmount.toLocaleString()}</span>
              </div>
              <div className={styles.comparisonRow}>
                <span className={styles.comparisonLabel}>Your monthly income:</span>
                <span className={styles.comparisonValue}>
                  {monthlyIncome > 0 ? `$${monthlyIncome.toLocaleString()}` : 'Not set'}
                </span>
              </div>
            </div>

            <div className={styles.statusContainer}>
              <div className={`${styles.statusCircle} ${styles[comparison.color]}`}></div>
              <p className={styles.statusMessage}>{comparison.message}</p>
            </div>

            <div className={styles.buttonContainer}>
              <Button onClick={handleComparisonComplete} className={styles.continueButton}>
                Continue to Complete Quest
              </Button>
              <Button onClick={handleClose} className={styles.cancelButton}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2 className={styles.title}>Guesstimate your monthly spending</h2>
        <p className={styles.subtitle}>
          Take your best guess - there's no wrong answer! This helps you start thinking about your spending patterns.
        </p>

        <div className={styles.optionsContainer}>
          {spendingOptions.map((option) => (
            <div key={option.id} className={styles.optionWrapper}>
              <label className={`${styles.optionLabel} ${selectedRange === option.value ? styles.selected : ''}`}>
                <input
                  type="radio"
                  name="spendingRange"
                  value={option.value}
                  checked={selectedRange === option.value}
                  onChange={(e) => setSelectedRange(e.target.value)}
                  className={styles.radioInput}
                />
                <span className={styles.optionText}>{option.label}</span>
                <span className={styles.checkmark}></span>
              </label>
            </div>
          ))}
        </div>

        <div className={styles.buttonContainer}>
          <Button
            onClick={handleComplete}
            disabled={!selectedRange}
            className={styles.completeButton}
          >
            Complete Quest
          </Button>
          <Button onClick={handleClose} className={styles.cancelButton}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SpendingGuessModal;